import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  Chip,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { ManagerStatisticsFile } from "../../../types/statistics";
import { sectionTitleStyles, cardStyles, cardContentStyles } from "../styles";

interface FilesSectionProps {
  files: ManagerStatisticsFile[];
  onDownloadFile: (file: ManagerStatisticsFile) => void;
}

export const FilesSection: React.FC<FilesSectionProps> = ({
  files,
  onDownloadFile,
}) => {
  const safeFiles = useMemo(() => files ?? [], [files]);

  const [searchTerm, setSearchTerm] = useState<string>("");

  const filteredFiles = useMemo(() => {
    if (!searchTerm) return safeFiles;
    const q = searchTerm.toLowerCase();
    return safeFiles.filter((f) => {
      const name = (f.name || "").toLowerCase();
      const type = (f.type || "").toLowerCase();
      return name.includes(q) || type.includes(q);
    });
  }, [safeFiles, searchTerm]);

  if (safeFiles.length === 0) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={sectionTitleStyles}>
        Reports & Documents
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        <TextField
          placeholder="Search reports..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ maxWidth: 420, flexGrow: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        <Chip
          label={`Found: ${filteredFiles.length} of ${safeFiles.length}`}
          color={
            filteredFiles.length < safeFiles.length ? "success" : "default"
          }
          variant="outlined"
          size="small"
        />
      </Box>
      <Grid container spacing={2}>
        {filteredFiles.map((file, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card sx={cardStyles}>
              <CardContent sx={cardContentStyles}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  {file.name}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {file.type.toUpperCase()} file
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => onDownloadFile(file)}
                  fullWidth
                >
                  Download
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
